import { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NoSymbolIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { NodeSummaryCards, AiAnalyticsCards, type AiAnalyticsSummary } from '@trinetra/shared';
import { useNode } from '../../contexts/NodeContext';

/**
 * How many analytics cards a collapsed row shows.
 *
 * Three, because that is what the priority order puts first — outstanding alerts, how they trended
 * over the last day, who is on site — and those three answer the only question the estate page
 * asks: which site needs me. Everything past them is detail for one site, which is what expanding
 * is for.
 */
const COLLAPSED_CARDS = 3;

/** The site's own tiles, shown only when the row is expanded. */
const NODE_CARDS = ['cameras-models', 'status'] as const;

/**
 * One registered node as a full-width row: an identity header, and the shared summary cards.
 *
 * The cards live in @trinetra/shared because the local product renders the same six for its one
 * node; this file is what makes them a CLOUD row — the online/offline state, the site actions
 * menu, and the fact that every click must SELECT this node before navigating. Those
 * destinations are node-scoped, so without the select they'd read whichever node was previously
 * selected (or render its "select a node" empty state) — ordering in `go` is load-bearing.
 *
 * Since a click also re-scopes the whole sidebar to this node, the row shows which node is
 * currently selected — otherwise the operator has no way to tell from the dashboard which node
 * the rest of the app is pointing at.
 */

/**
 * Soft tinted background + solid text from a CSS-var colour.
 *
 * `bg-success/15` does NOT work here: the theme maps these tokens as full colour
 * values (`success: 'var(--success)'`), and Tailwind's opacity modifier can't
 * decompose that into channels — the rule silently produces nothing. `color-mix`
 * is the pattern the attendance Timeline already uses for the same reason.
 */
const tint = (c: string) => ({
  backgroundColor: `color-mix(in srgb, ${c} 15%, transparent)`,
  color: c,
});

export interface NodeDetail {
  cameras: any[];
  groups: any[];
  /** getNodeModelAssignments payload. */
  assignments: any;
  /** Latest alerts, newest first. */
  alerts: any[];
  loading: boolean;
  /**
   * /analytics/summary for this site, or null when it hasn't been read (no permission, offline,
   * or the call failed). Fetched by the page, like everything else here — one place owns the
   * per-site fan-out and its cadence.
   */
  ai?: AiAnalyticsSummary | null;
  /** True while the AI summary specifically is still in flight. */
  aiLoading?: boolean;
  /** Why the AI summary couldn't be read, if it couldn't. Shown in place of the strip. */
  aiError?: string;
  /**
   * The active surfaces the summary was REQUESTED with. Kept alongside the answer rather than
   * re-derived here: the strip must not draw a figure for a model the request never asked about.
   */
  aiSurfaces?: string[];
}

interface NodeInfo {
  nodeId: string;
  name: string;
  status: 'online' | 'offline';
  tunnelActive: boolean;
  lastSeen: number;
  metrics: any;
  version: string;
  activeSessions: number;
}

interface NodeSummaryRowProps {
  node: NodeInfo;
  detail?: NodeDetail;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  hasPermission: (p: string) => boolean;
}

/** Camera status → bucket, matching the dashboard tiles and the local dashboard. */
const CAMERA_BUCKETS: Record<string, 'online' | 'connecting' | 'error'> = {
  streaming: 'online',
  recording: 'online',
  connecting: 'connecting',
  error: 'error',
};

export default function NodeSummaryRow({
  node,
  detail,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  hasPermission,
}: NodeSummaryRowProps) {
  const navigate = useNavigate();
  const { selectNode, selectedNode } = useNode();
  const [menuOpen, setMenuOpen] = useState(false);
  /**
   * Collapsed by default, per row.
   *
   * Not lifted to the page: an operator opens the one site they are investigating, and remembering
   * that choice across the others (or across a refresh) would mean an estate page that quietly
   * grows every card it was designed to keep out.
   */
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOnline = node.status === 'online';
  const isSelected = selectedNode?.nodeId === node.nodeId;
  const loading = detail?.loading ?? isOnline;

  /**
   * Left accent bar is purely status — it is NOT overridden for the selected row.
   * Selection is signalled by the row's grey border alone (plus the neutral
   * "Selected" pill); nothing about the header or the title changes colour.
   */
  const accent = isOnline ? 'var(--success)' : 'var(--destructive)';

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  /** Select this node first, then navigate — see the note on the component. */
  const go = (path: string) => {
    selectNode(node.nodeId);
    navigate(path);
  };

  const canAiEvents = hasPermission('ai_events.view');

  /** camera id → name, for the alert lines. Already fetched for the cameras tile. */
  const cameraNames = useMemo(
    () => Object.fromEntries(((detail?.cameras || []) as any[]).map((c) => [c.id, c.name]).filter(([, nm]) => nm)),
    [detail?.cameras],
  );

  /** Shared by both branches of the body, so the tiles are identical either way. */
  const nodeCardProps = {
    data: {
      cameras: detail?.cameras || [],
      groups: detail?.groups || [],
      assignments: detail?.assignments || {},
      alerts: detail?.alerts || [],
      metrics: node.metrics || {},
      loading,
    },
    links: {
      cameras: hasPermission('cameras.view') ? '/camera-management' : undefined,
      recording: hasPermission('recordings.view') ? '/recordings' : undefined,
      alerts: hasPermission('alerts.view') ? '/alerts' : undefined,
      models: hasPermission('ai-models.assign') ? '/model-assignments' : undefined,
      groups: hasPermission('groups.view') ? '/camera-groups' : undefined,
      status: hasPermission('monitoring.view') ? `/node-monitor/${node.nodeId}` : undefined,
    },
    onNavigate: go,
  };

  /**
   * Every card destination is node-scoped, and `go` selects this site before routing — so a click
   * on a card in THIS row can't land on whichever site was previously selected.
   */
  const aiLinks = {
    reports: {
      face: '/reports/face',
      person: '/reports/person',
      'person-count': '/reports/person-count',
      intrusion: '/reports/intrusion',
      'vehicle-crossing': '/reports/vehicle-crossing',
      'ppe-violation': '/reports/ppe-violation',
      theft: '/reports/theft',
      fall: '/reports/fall',
      'fire-smoke': '/reports/fire-smoke',
    },
    alerts: hasPermission('alerts.view') ? '/alerts' : undefined,
    attendance: hasPermission('attendance.view') ? '/attendance/presence' : undefined,
  };

  /**
   * Clicking the row selects this node in the sidebar dropdown and opens Live View.
   *
   * Only wired when the user can actually reach /cameras — otherwise the route guard
   * bounces them straight back to the dashboard and the click looks broken.
   *
   * This is a mouse-convenience layer, NOT the accessible control: the row stays a
   * plain div (no role="button") because it contains six focusable tiles, and nesting
   * a button role around other buttons breaks assistive navigation. Keyboard users get
   * the same action from the node-name button inside the header.
   */
  const rowOpensLiveView = hasPermission('cameras.view');

  return (
    <div
      onClick={rowOpensLiveView ? () => go('/cameras') : undefined}
      className={`group/row relative overflow-hidden rounded-xl border bg-card shadow-card transition-all ${
        rowOpensLiveView ? 'cursor-pointer' : ''
      } ${
        // Selected supplies its own borderColor below, so no class here.
        isSelected ? '' : isOnline ? 'border-border' : 'border-border/60'
      }`}
      // Selected = a plain darker GREY border. Deliberately neutral: no brand ring,
      // no header tint, no accent-bar override, so nothing about the node title
      // changes colour when a row becomes the selected one.
      style={
        isSelected
          ? { borderColor: 'color-mix(in srgb, var(--muted-foreground) 55%, transparent)' }
          : undefined
      }
    >
      {/* Status accent — full-height so the row is scannable from the left edge. */}
      <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: accent }} aria-hidden />

      {/* ── Row header ── no selected-state tint; the header looks identical whether
          or not this row is the selected one. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border pl-5 pr-3 py-2.5">
        {/* The keyboard-reachable equivalent of the row click. `group-hover/row`
            highlights it from anywhere on the row, so the whole row visibly reads as
            "opens Live View" without needing its own hover background. */}
        <button
          onClick={(e) => { e.stopPropagation(); go('/cameras'); }}
          className="min-w-0 truncate text-[15px] font-semibold text-foreground transition-colors hover:text-brand group-hover/row:text-brand"
          title="Select this site and open its Live View"
        >
          {node.name}
        </button>

        {/* State as a pill: dot + word, so it never depends on colour alone. */}
        <span
          className="flex flex-none items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
          style={tint(isOnline ? 'var(--success)' : 'var(--destructive)')}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${isOnline ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: isOnline ? 'var(--success)' : 'var(--destructive)' }}
            aria-hidden
          />
          {isOnline ? 'Online' : 'Offline'}
        </span>

        {/* Kept, in neutral grey: a 1px border-colour shift is easy to miss when rows
            can't be compared side by side, and this is the only cue that the sidebar
            is scoped to this node. */}
        {isSelected && (
          <span
            className="flex-none rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
            style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 15%, transparent)' }}
          >
            Selected
          </span>
        )}

        {!isOnline && (
          <span className="flex-none text-[11px] text-muted-foreground">last seen {formatLastSeen(node.lastSeen)}</span>
        )}

        <div className="flex-1" />

        <div className="flex flex-none items-center gap-2">
          {node.tunnelActive && (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium" style={tint('var(--brand)')}>
              Tunnel
            </span>
          )}
          {node.version && (
            <span className="hidden text-[11px] tabular-nums text-muted-foreground sm:inline">v{node.version}</span>
          )}

          {/* Expand this site. stopPropagation like every other control in here: the row itself
              navigates to Live View, and opening a site's detail must not do that too. */}
          {isOnline && (
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              className="flex items-center gap-1 rounded-lg px-1.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title={expanded ? 'Show less' : 'Show all cards for this site'}
              aria-expanded={expanded}
            >
              <span className="hidden sm:inline">{expanded ? 'Less' : 'More'}</span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}

          {/* Every control in here stops propagation — opening the menu or picking
              Edit/Delete must not also navigate away to Live View. */}
          {(canEdit || canDelete) && (
            <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                title="Site actions"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-9 z-50 w-44 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-dropdown">
                  {canEdit && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit?.(); }}
                      className="w-full px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
                    >
                      Edit Node
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete?.(); }}
                      className="w-full px-4 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
                    >
                      Delete Node
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Mini cards ── */}
      {!isOnline ? (
        // Recessed panel matching the tiles, so an offline row reads as part of the
        // set rather than a blank gap in the sheet.
        <div className="p-3 pl-5">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-3 text-[12px] text-muted-foreground">
            <NoSymbolIcon className="h-4 w-4 flex-none" />
            <span>Offline — cameras, alerts and metrics are unavailable until the site reconnects.</span>
          </div>
        </div>
      ) : (
        // No background here — the row is ONE `bg-card` sheet and the tiles are recessed into it
        // (see MiniCard). Do NOT tint this `bg-background`: that is the page colour, so the row's
        // interior would match the page and the card boundary would dissolve into a floating
        // header strip with loose tiles.
        //
        // The cards themselves are shared with the local node's own dashboard — same six tiles,
        // same data contract. What differs is here: each click must SELECT this node before
        // routing, or the destination reads whichever site was previously selected.
        <>
        {/* Why the summary couldn't be read, when it couldn't. Kept visible rather than hidden: on
            an estate page a missing strip of figures is indistinguishable from a site that runs no
            models, and "offline", "timed out" and "permission denied" need different answers. */}
        {detail?.aiError && (
          <div className="px-3 pl-5">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-[11px] text-muted-foreground">
              <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-none" style={{ color: 'var(--warning)' }} />
              <span className="min-w-0 truncate" title={detail.aiError}>
                AI summary unavailable — {detail.aiError}
              </span>
            </div>
          </div>
        )}

        {/* The SAME cards the site dashboard draws, from the same shared component — capped to the
            first few while collapsed. The site's own tiles (cameras & models, machine health) are
            withheld entirely until expanded: they are the least urgent thing on an estate page and
            they are what makes a row tall. */}
        {canAiEvents ? (
          <AiAnalyticsCards
            className="p-3 pl-5"
            surfaces={detail?.aiSurfaces || []}
            summary={detail?.ai ?? null}
            latestAlerts={hasPermission('alerts.view') ? detail?.alerts : undefined}
            cameraNames={cameraNames}
            loading={!!detail?.aiLoading}
            limit={expanded ? undefined : COLLAPSED_CARDS}
            links={aiLinks}
            onNavigate={go}
            trailing={expanded ? <NodeSummaryCards bare cards={[...NODE_CARDS]} {...nodeCardProps} /> : undefined}
          />
        ) : (
          /* No AI permission: the site's own tiles are all there is, so they show regardless of the
             expanded state rather than disappearing with the section that would have hosted them. */
          <NodeSummaryCards className="p-3 pl-5" cards={[...NODE_CARDS]} {...nodeCardProps} />
        )}
        </>
      )}
    </div>
  );
}

/* ─── Formatting ─── */

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function formatLastSeen(ts: number): string {
  if (!ts) return 'never seen';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
